import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// HIPAA idle timeout - 15 minutes of inactivity
const IDLE_TIMEOUT = 15 * 60 * 1000;

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const idleTimerRef = useRef(null);

  // HIPAA idle timeout - auto-logout after 15 minutes of inactivity
  useEffect(() => {
    if (!isAuthenticated) return;

    const resetTimer = () => {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        localStorage.removeItem('agencyToken');
        api.post('/agency/logout').catch(() => {}).finally(() => {
          window.location.href = '/login';
        });
      }, IDLE_TIMEOUT);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(e => document.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(idleTimerRef.current);
      events.forEach(e => document.removeEventListener(e, resetTimer));
    };
  }, [isAuthenticated]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          textAlign: 'center',
          p: 3,
        }}
      >
        <Typography variant="h4" sx={{ mb: 2, color: '#dc2626' }}>
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
          You don't have permission to access this page.
        </Typography>
        <Typography
          component="a"
          href="/dashboard"
          sx={{ color: '#06AB8B', textDecoration: 'none' }}
        >
          Go to Dashboard
        </Typography>
      </Box>
    );
  }

  return children;
};

export default ProtectedRoute;
